/**
 * folderClassify.ts — Phase 7.8 folder-upload classifier.
 *
 * Pure data-transformation utility. Given a FileList from a
 * <input type="file" webkitdirectory multiple> picker, partition each file
 * into: route to MIND (markdown/text/code), route to SIGHT (images),
 * or skip with an honest reason (unsupported extension / over-size).
 *
 * Per-file caps mirror backend wire limits enforced by Pydantic + nginx:
 *   MAX_IMAGE_BYTES = 5MB (mirrors KnowledgeIngestPanel image picker cap)
 *   MAX_TEXT_BYTES  = 20MB (mirrors POST /api/knowledge/ingest content cap)
 *
 * Aggregate caps (100 files / 100MB) enforced by the caller (store action
 * + UI banner), NOT here — this utility is per-file pure.
 */

export type TargetService = 'mind' | 'sight';
// 'pdf' files are uploaded as raw bytes to POST /api/knowledge/ingest/pdf,
// which extracts the text layer server-side (pypdf) then ingests into MIND.
// All other contentTypes are read client-side (text or data-URL).
export type ContentType = 'markdown' | 'text' | 'image' | 'pdf';

export interface ClassifiedFile {
  file: File;
  sourceName: string;
  targetService: TargetService | null;
  contentType: ContentType | null;
  skipReason: string | null;
}

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_TEXT_BYTES = 20 * 1024 * 1024;
// PDF upload cap mirrors backend _MAX_PDF_BYTES (pre-extraction byte cap).
export const MAX_PDF_BYTES = 25 * 1024 * 1024;

// Aggregate folder caps enforced by runIngestFolder (NOT this classifier).
// FOLDER_MAX_FILES counts only supported files (skipped don't count).
// FOLDER_MAX_TOTAL_BYTES sums supported-file sizes only.
export const FOLDER_MAX_FILES = 100;
export const FOLDER_MAX_TOTAL_BYTES = 100 * 1024 * 1024;
export const FOLDER_CONCURRENCY = 5;

const IMAGE_EXTS = new Set(['.png', '.jpg', '.jpeg', '.gif']);
const PDF_EXTS = new Set(['.pdf']);
const MARKDOWN_EXTS = new Set(['.md']);
const TEXT_EXTS = new Set([
  '.txt', '.py', '.ts', '.tsx', '.js', '.json',
  '.yaml', '.yml', '.toml', '.sh', '.go', '.rs',
]);

export function classifyFolderFiles(files: File[]): ClassifiedFile[] {
  return files.map(classifyOne);
}

function classifyOne(file: File): ClassifiedFile {
  const sourceName = file.webkitRelativePath || file.name;
  const lower = file.name.toLowerCase();
  const dotIdx = lower.lastIndexOf('.');
  const ext = dotIdx >= 0 ? lower.slice(dotIdx) : '';

  if (IMAGE_EXTS.has(ext)) {
    if (file.size > MAX_IMAGE_BYTES) {
      return mkSkip(file, sourceName, `image exceeds 5MB cap (${formatMB(file.size)})`);
    }
    return { file, sourceName, targetService: 'sight', contentType: 'image', skipReason: null };
  }
  if (PDF_EXTS.has(ext)) {
    if (file.size > MAX_PDF_BYTES) {
      return mkSkip(file, sourceName, `PDF exceeds 25MB cap (${formatMB(file.size)})`);
    }
    return { file, sourceName, targetService: 'mind', contentType: 'pdf', skipReason: null };
  }
  if (MARKDOWN_EXTS.has(ext)) {
    if (file.size > MAX_TEXT_BYTES) {
      return mkSkip(file, sourceName, `text exceeds 20MB cap (${formatMB(file.size)})`);
    }
    return { file, sourceName, targetService: 'mind', contentType: 'markdown', skipReason: null };
  }
  if (TEXT_EXTS.has(ext)) {
    if (file.size > MAX_TEXT_BYTES) {
      return mkSkip(file, sourceName, `text exceeds 20MB cap (${formatMB(file.size)})`);
    }
    return { file, sourceName, targetService: 'mind', contentType: 'text', skipReason: null };
  }
  return mkSkip(file, sourceName, `unsupported extension: ${ext || '(none)'}`);
}

function mkSkip(file: File, sourceName: string, reason: string): ClassifiedFile {
  return { file, sourceName, targetService: null, contentType: null, skipReason: reason };
}

function formatMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}
